// 관세감면 검색 — 검색어를 감면 조문·감면부호에 잇는다(문의 #76).
// "대사관" → 제88조 외교관용물품 등의 면세, "학술" → 제90조, "장애인" → 제91조.
// 글자 비교만 한다(LLM·판정 없음). 조문 낱말은 서버 customs_exemption_ref.json 의 keywords.

const norm = (s) => String(s == null ? "" : s).toLowerCase().replace(/\s+/g, "");

// 부호 목록을 화면 묶음으로: 조문이 있는 관세법 부호는 그 조문에, 나머지는 근거 법령별 묶음으로.
export function exemptionGroups(articles, codes) {
  const known = new Set((articles || []).map((a) => a.article));
  const groups = new Map();
  (codes || []).forEach((c) => {
    if (c.law === "관세법" && known.has(c.art)) return;
    const key = c.law === "관세법" ? `관세법 제${c.art}조` : c.law;
    // section: "tax" = 내국세 감면·면세 부호(부가세·개별소비세…), "other" = 분할납부·다른 법률·협정
    if (!groups.has(key)) groups.set(key, { key, title: key, section: c.tax ? "tax" : "other", codes: [] });
    groups.get(key).codes.push(c);
  });
  return [...groups.values()];
}

export function codesOfArticle(codes, article) {
  return (codes || []).filter((c) => c.law === "관세법" && c.art === article);
}

// 검색어 → { articles:[{article, via}], groups:[key], codes:[code] }
// via = 제목·대상·낱말 중 무엇에 걸렸는지("keyword" | "text" | "code").
export function searchExemptions(q, articles, codes) {
  const n = norm(q);
  const empty = { articles: [], groups: [], codes: [] };
  if (n.length < 2) return empty;
  const hitArticles = new Map();
  (articles || []).forEach((a) => {
    const kw = (a.keywords || []).some((k) => {
      const kk = norm(k);
      return kk && (n.includes(kk) || kk.includes(n));
    });
    if (kw) hitArticles.set(a.article, "keyword");
    else if (norm(`${a.title}${a.target}제${a.article}조`).includes(n)) hitArticles.set(a.article, "text");
  });
  const known = new Set((articles || []).map((a) => a.article));
  const hitCodes = [];
  const hitGroups = new Set();
  (codes || []).forEach((c) => {
    // 부호 번호 · 부호 내용 · 그 부호가 가리키는 법령 원문("대사관ㆍ공사관 … 업무용품")에서 찾는다.
    const lawText = (c.texts || []).map((t) => `${t.text} ${(t.items || []).join(" ")}`).join(" "); // 별표 세부 품목("보청기")까지
    if (!norm(c.code).includes(n) && !norm(c.content).includes(n) && !norm(lawText).includes(n)) return;
    hitCodes.push(c.code);
    if (c.law === "관세법" && known.has(c.art)) {
      if (!hitArticles.has(c.art)) hitArticles.set(c.art, "code");
    } else {
      hitGroups.add(c.law === "관세법" ? `관세법 제${c.art}조` : c.law);
    }
  });
  return {
    articles: [...hitArticles.entries()].map(([article, via]) => ({ article, via })),
    groups: [...hitGroups],
    codes: hitCodes,
  };
}

// 통합 검색 결과 위에 보일 한 줄. 세번 검색을 방해하지 않도록 좁게 건다:
// 검색어 전체가 조문 낱말과 같거나("대사관"), 검색어에 감면·면세가 들어 있으면서 낱말을 품을 때("학술 감면").
// "연구용 시약"·"가공식품"처럼 낱말을 품기만 한 물품 검색에는 나오지 않는다.
export function exemptionHints(q, articles) {
  const n = norm(q);
  if (n.length < 2 || /^\d+$/.test(n)) return [];
  const asksRelief = /감면|면세/.test(n);
  return (articles || []).filter((a) =>
    (a.keywords || []).some((k) => {
      const kk = norm(k);
      if (kk.length < 2) return false;
      return n === kk || (asksRelief && n.includes(kk));
    }),
  );
}

// 검색어가 감면부호 꼴인가 — "A088000101" · "Y6701" · "A0900001020H". 부호 목록을 받기 전의 빠른 판별.
export function looksLikeExemptionCode(q) {
  const t = String(q == null ? "" : q).trim();
  return /^[a-z][0-9a-z]{4,11}$/i.test(t) && /\d{3}/.test(t);
}

// 검색어가 감면부호와 정확히 같으면 그 부호(대소문자 무시). 물품 검색이 아니므로 결정트리·제안을 돌리지 않는다.
export function findExemptionCode(q, codes) {
  if (!looksLikeExemptionCode(q)) return null;
  const t = String(q).trim().toUpperCase();
  return (codes || []).find((c) => String(c.code).toUpperCase() === t) || null;
}

// 부호 하나가 놓인 관세감면 화면 주소 조각 — 조문이 있으면 art, 아니면 근거 묶음 grp.
export function exemptionTargetOf(code, articles) {
  const known = new Set((articles || []).map((a) => a.article));
  if (code.law === "관세법" && known.has(code.art)) return { art: String(code.art) };
  return { grp: code.law === "관세법" ? `관세법 제${code.art}조` : code.law };
}

// 부호 꼴인데 목록에 없을 때 — 앞 4글자가 같은 부호들(문의 #87: A09100000421 을 물품으로 읽어 자동차 트리가 돌았다).
// 앞자리가 같은 부호가 하나도 없으면 부호 검색으로 보지 않는다("MX3000" 같은 모델명을 막지 않기 위해).
export function nearestExemptionCodes(q, codes) {
  if (!looksLikeExemptionCode(q)) return [];
  const t = String(q).trim().toUpperCase();
  const head = t.slice(0, 4);
  const common = (c) => {
    let i = 0;
    while (i < Math.min(c.length, t.length) && c[i] === t[i]) i += 1;
    return i;
  };
  return (codes || [])
    .filter((c) => String(c.code).toUpperCase().startsWith(head))
    .sort((a, b) => common(String(b.code).toUpperCase()) - common(String(a.code).toUpperCase()))
    .slice(0, 5);
}

let listPromise = null;
// 감면 조문·부호 — 화면 사이에 한 번만 받는다. 실패는 빈 값(fail-open)이고 다음 호출에서 다시 시도.
export function loadExemptionList() {
  if (!listPromise) {
    listPromise = fetch("/api/onto/exemption/list")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => ({
        items: d.items || [],
        // 내국세 감면·면세 부호는 세목별 묶음으로 같은 목록에 싣는다 — 검색·부호 검색·표가 그대로 쓴다.
        codes: [
          ...(d.codes || []),
          ...(d.tax_codes || []).map((c) => ({ ...c, law: `${c.tax} 감면·면세 부호` })),
        ],
        source: d.codes_source || null,
        taxSource: d.tax_codes_source || null,
        lawArticles: d.law_articles || {},
        lawSources: d.law_sources || {},
      }))
      .catch(() => {
        listPromise = null;
        return { items: [], codes: [], source: null, lawArticles: {}, lawSources: {} };
      });
  }
  return listPromise;
}

export function resetExemptionListCache() {
  listPromise = null;
}

// 부호 내용에 적힌 근거("관세법 제90조제1항제2호 해당물품중 …")로 그 부호가 붙는 항·호를 읽는다.
// 항이 없는 조문(제91조는 바로 각 호로 간다)도 받는다. 실측 347개 중 346개가 잡히고, 남은 하나는
// "각호 이외의 기관 수입물품"이라 어느 호에도 속하지 않는 것이 맞다.
export function codeClauseOf(content, article) {
  const m = String(content || "").match(/제(\d+)조(?:의\d+)?\s*(?:제(\d+)항)?\s*(?:제(\d+)(?:의(\d+))?호)?/);
  if (!m || Number(m[1]) !== Number(article)) return null;
  const hang = m[2] ? Number(m[2]) : null;
  const ho = m[3] ? Number(m[3]) : null;
  if (hang == null && ho == null) return null;
  return { hang, ho, branch: m[4] ? Number(m[4]) : null };
}

// 조문 원문(lawArticles[art].body)에서 그 항·호의 글을 꺼낸다 — 새로 받아오지 않는다.
export function clauseText(body, clause) {
  if (!body || !clause) return null;
  const para = clause.hang ? body[clause.hang - 1] : body[0];
  if (!para) return null;
  if (clause.ho == null) return para.text || null;
  const item = (para.items || [])[clause.ho - 1];
  return item || null;
}

// 부호를 항·호로 묶는다. 순서는 항 → 호, 어디에도 안 붙는 것은 맨 뒤.
export function groupCodesByClause(codes, article) {
  const buckets = new Map();
  (codes || []).forEach((c) => {
    const cl = codeClauseOf(c.content, article);
    const key = cl ? `${cl.hang ?? 0}-${cl.ho ?? 0}-${cl.branch ?? 0}` : "";
    if (!buckets.has(key)) buckets.set(key, { key, clause: cl, codes: [] });
    buckets.get(key).codes.push(c);
  });
  return [...buckets.values()].sort((a, b) => {
    if (!a.clause) return 1;
    if (!b.clause) return -1;
    return (
      (a.clause.hang ?? 0) - (b.clause.hang ?? 0) ||
      (a.clause.ho ?? 0) - (b.clause.ho ?? 0) ||
      (a.clause.branch ?? 0) - (b.clause.branch ?? 0)
    );
  });
}

// "제1항제2호" · "제4호의2" — 묶음 머리에 쓰는 이름.
export function clauseLabel(clause) {
  if (!clause) return "어느 호에도 속하지 않음";
  const hang = clause.hang ? `제${clause.hang}항` : "";
  const ho = clause.ho ? `제${clause.ho}${clause.branch ? `의${clause.branch}` : ""}호` : "";
  return hang + ho;
}

// 호 머리글이 이미 말한 문장을 부호마다 되풀이하지 않는다(문의 #101). 부호 내용은
// "관세법 제90조제1항제2호 해당물품중 시행규칙 제37조제2항제1호 기관에서 사용할 물품" 꼴이라,
// 앞의 "…해당물품중"까지는 호 머리글과 같은 말이다. 그 뒤가 이 부호를 가르는 자리다.
export function codeDistinctPart(content) {
  const t = String(content || "").trim();
  const m = t.match(/해당물품\s*중\s*/);
  if (!m) return t;
  const rest = t.slice(m.index + m[0].length).trim();
  return rest || t;              // 뒤가 비면 원문을 그대로 둔다 — 지워서 빈칸을 만들지 않는다
}

// 부호가 가리키는 가장 좁은 조문 — 시행규칙 호가 있으면 그것이 이 부호를 가르는 자리다.
export function narrowestText(code) {
  const texts = (code && code.texts) || [];
  return texts.length ? texts[texts.length - 1] : null;
}

const bare = (t) => String(t || "").replace(/^\s*\d+(?:의\d+)?\.\s*/, "").replace(/\s+/g, "");

// 호 머리글이 부호 줄과 같은 말을 하는가. 제88조 제1항처럼 호마다 부호가 하나뿐이면 원문이 글자까지
// 같아 두 번 읽게 된다(사용자 지적). 그럴 때만 머리글을 걷고 부호 줄에 호 이름을 배지로 붙인다.
// 제88조 제2항처럼 부호마다 시행규칙 호가 달라 내용이 다르면(자동차·선박·피아노…) 머리글을 남긴다.
export function clauseHeadIsRedundant(group, body) {
  const head = bare(clauseText(body, group && group.clause));
  if (!head || !group.codes || group.codes.length === 0) return false;
  return group.codes.every((c) => {
    const t = narrowestText(c);
    return t && bare(t.text) === head;
  });
}

// "관세법 제88조제1항제1호" → "법 1항1호", "시행규칙 제37조제2항제1호" → "규칙 2항1호".
// 제90조는 법의 호와 시행규칙의 호가 한 표에 섞여 있어(법 1항2호 ↔ 규칙 2항1호) 어느 쪽인지 밝혀야 한다.
export function shortRef(ref) {
  const t = String(ref || "");
  if (!t) return null;
  const kind = /시행규칙/.test(t) ? "규칙" : /시행령/.test(t) ? "영" : "법";
  const hang = t.match(/제(\d+)항/);
  const ho = t.match(/제(\d+)(?:의(\d+))?호/);
  const tail = `${hang ? `${hang[1]}항` : ""}${ho ? `${ho[1]}${ho[2] ? `의${ho[2]}` : ""}호` : ""}`;
  return tail ? `${kind} ${tail}` : kind;
}

// 부호 내용에서 조문 참조를 걷고도 남는 말이 있는가. "관세법 제88조제1항제1호 해당물품"은 근거 호 열이
// 이미 말하므로 지우고, "…해당물품중 공공의료기관 및 학교부설의료기관에서 사용할 물품"처럼 부호를
// 가르는 말이 남으면 그것만 보인다. 지워서 빈칸을 만들지 않는다 — 남는 말이 없을 때만 지운다.
export function codeExtraNote(content) {
  const part = codeDistinctPart(content);
  const stripped = String(part || "")
    .replace(/(관세법|자유무역협정[^\s]*|조세특례제한법|시행규칙|시행령|법)?\s*제\s*\d+\s*조(?:의\d+)?(?:\s*제\s*\d+\s*항)?(?:\s*제\s*\d+(?:의\d+)?\s*호)?/g, "")
    .replace(/해당물품|물품|중|및|\s/g, "");
  return stripped ? part : null;
}

// S·H 로 끝나는 부호는 시행규칙의 개별 기관 하나가 아니라 기관을 묶은 것이라 실무에서 먼저 본다.
// 묶음 안에서만 위로 올리고, 나머지는 종전처럼 번호순이다(문의 #101).
export function sortCodesForDisplay(codes) {
  const grouped = (c) => /[SH]$/.test(c.code || "");
  return [...(codes || [])].sort((a, b) => {
    if (grouped(a) !== grouped(b)) return grouped(a) ? -1 : 1;
    return String(a.code).localeCompare(String(b.code));
  });
}
